import { useState, useEffect, useCallback } from 'react';
import { useUrlParam, useUrlPanelSync } from '../../../hooks/useUrlParam';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBoxArchive, faPlus, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { Client } from '../../../context/ClientContext';
import { useNavigate } from 'react-router-dom';

import ExistingTasksList from './functions/ExistingTasksList';
import BacklogTasksList from './functions/BacklogTasksList';
import TaskDetailSidepanel from './functions/TaskDetailSidepanel';
import ArchivedTasksSidepanel from './functions/ArchivedTasksSidepanel';
import {
  safeSupabaseQuery,
  checkAdminStatus,
  fetchUsers,
  fetchClientTasks,
  updateTask,
} from './functions/TasksUtils';
import DeleteConfirmationModal from './functions/DeleteConfirmationModal';
import { supabase } from '../../../lib/supabase';
import CreateTaskSidepanel from './CreateTaskSidepanel';
import confetti from 'canvas-confetti';
import { Button, Card } from '../../ui';
import type { ClientTaskRow } from './functions/taskMeta';

interface User {
  id: string;
  name: string;
  email: string;
}

interface TasksSectionProps {
  client: Client;
  expandedSection: string | null;
  toggleSection: (section: string) => void;
  onTaskAdded?: () => void;
  onViewTaskDetails?: (task: ClientTaskRow) => void;
}

interface UpdatedTaskInput {
  title?: string;
  status?: string;
  dueDate?: string | null;
  assignedTo?: string | null;
  assignedToName?: string | null;
}

export default function TasksSection({
  client,
  expandedSection,
  toggleSection,
  onTaskAdded,
}: TasksSectionProps) {
  const navigate = useNavigate();
  const [isTaskSidepanelOpen, setIsTaskSidepanelOpen] = useState(false);
  const [clientTasks, setClientTasks] = useState<ClientTaskRow[]>([]);
  const [backlogTasks, setBacklogTasks] = useState<ClientTaskRow[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [isTaskDetailSidepanelOpen, setIsTaskDetailSidepanelOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<ClientTaskRow | null>(null);
  // The open task is ?task=<id>: clicking a task sets it, closing clears it, and the URL drives the panel.
  const [urlTask, setUrlTask] = useUrlParam('task');
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [taskToArchive, setTaskToArchive] = useState<string | null>(null);
  const [isArchivingTask, setIsArchivingTask] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [isArchivedSidepanelOpen, setIsArchivedSidepanelOpen] = useState(false);
  const [archivedTasks, setArchivedTasks] = useState<ClientTaskRow[]>([]);
  const [isLoadingArchivedTasks, setIsLoadingArchivedTasks] = useState(false);
  const [archivedTasksError, setArchivedTasksError] = useState<string | null>(null);
  const [permanentDeleteModalOpen, setPermanentDeleteModalOpen] = useState(false);
  const [taskToPermanentlyDelete, setTaskToPermanentlyDelete] = useState<string | null>(null);
  const [isDeletingArchivedTask, setIsDeletingArchivedTask] = useState(false);

  const clientId = client?.id;

  // Check authentication status before fetching data
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        setIsLoading(true);
        const { isAuthenticated, isAdmin } = await checkAdminStatus();

        if (!isAuthenticated) {
          navigate('/login');
          return;
        }

        setIsAdmin(isAdmin);
      } catch (error) {
        console.error('Error in initializeAuth:', error);
        setFetchError('Network error while checking authentication. Please check your connection.');
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, [navigate]);

  // Fetch client tasks
  useEffect(() => {
    const loadClientTasks = async () => {
      if (!clientId) {
        console.error('Client ID is undefined');
        return;
      }

      try {
        setIsLoading(true);

        // Fetch users data
        const { data: usersData, error: usersError } = await fetchUsers();
        if (usersError) {
          console.error('Error fetching users:', usersError);
        } else {
          setUsers((usersData || []).map((u) => ({ ...u, name: u.name ?? u.email })));
        }

        const { data, error } = await fetchClientTasks(clientId);

        if (error) {
          if (error === 'No authenticated session') {
            navigate('/login');
            return;
          }
          setFetchError(`Failed to load client tasks: ${error}`);
          return;
        }

        const rows = (data || []) as ClientTaskRow[];
        const assigned = rows
          .filter((t) => t.assigned_to)
          .sort((a, b) => (a.client_tasks_priority ?? 0) - (b.client_tasks_priority ?? 0));
        const backlog = rows
          .filter((t) => !t.assigned_to)
          .sort((a, b) => (a.client_backlog_priority ?? 0) - (b.client_backlog_priority ?? 0));
        setClientTasks(assigned);
        setBacklogTasks(backlog);
      } catch (error) {
        console.error('Error in loadClientTasks:', error);
        setFetchError('Network error while loading client tasks. Please check your connection and try again.');
      } finally {
        setIsLoading(false);
      }
    };

    if (expandedSection === 'tasks') {
      loadClientTasks();
    }
  }, [clientId, navigate, expandedSection]);

  const handleStatusChange = async (taskId: string, newStatus: string) => {
    const oldStatus = [...clientTasks, ...backlogTasks].find((task) => task.id === taskId)?.status;

    try {
      const { error } = await safeSupabaseQuery(() =>
        supabase.from('client_tasks').update({ status: newStatus }).eq('id', taskId)
      );

      if (error) {
        console.error('Error updating status:', error);
        setFetchError('Failed to update task status. Please try again.');
        return;
      }

      // Update local state
      setClientTasks((currentTasks) =>
        currentTasks.map((task) => (task.id === taskId ? { ...task, status: newStatus } : task))
      );
      setBacklogTasks((currentTasks) =>
        currentTasks.map((task) => (task.id === taskId ? { ...task, status: newStatus } : task))
      );

      // Fire confetti if task was just completed or submitted to the client
      if ((newStatus === 'Complete' || newStatus === 'Submitted') && oldStatus !== newStatus) {
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 },
          colors: ['#d95636', '#fbbf24', '#10b981', '#3b82f6', '#8b5cf6'],
        });
      }
    } catch (error) {
      console.error('Error updating task status:', error);
      setFetchError('Network error while updating task status. Please check your connection.');
    }
  };

  const loadArchivedTasks = useCallback(async () => {
    if (!clientId) {
      return;
    }

    try {
      setIsLoadingArchivedTasks(true);
      setArchivedTasksError(null);

      const { data, error } = await fetchClientTasks(clientId, { archived: true });

      if (error) {
        if (error === 'No authenticated session') {
          navigate('/login');
          return;
        }
        setArchivedTasksError(`Failed to load archived tasks: ${error}`);
        return;
      }

      const sortedTasks = ((data || []) as ClientTaskRow[]).sort((a, b) => {
        const aDate = a.archived_at || a.updated_at || a.created_at;
        const bDate = b.archived_at || b.updated_at || b.created_at;
        return new Date(bDate || 0).getTime() - new Date(aDate || 0).getTime();
      });

      setArchivedTasks(sortedTasks);
    } catch (error) {
      console.error('Error loading archived tasks:', error);
      setArchivedTasksError('Failed to load archived tasks. Please try again.');
    } finally {
      setIsLoadingArchivedTasks(false);
    }
  }, [clientId, navigate]);

  useEffect(() => {
    if (isArchivedSidepanelOpen) {
      loadArchivedTasks();
    }
  }, [isArchivedSidepanelOpen, loadArchivedTasks]);

  const openArchiveModal = (taskId: string) => {
    setTaskToArchive(taskId);
    setArchiveModalOpen(true);
  };

  const handleArchiveTask = async (taskId: string) => {
    try {
      setIsArchivingTask(true);
      const { error } = await safeSupabaseQuery(() =>
        supabase
          .from('client_tasks')
          .update({ archived: true, archived_at: new Date().toISOString() })
          .eq('id', taskId)
      );

      if (error) {
        console.error('Error archiving task:', error);
        setFetchError('Failed to archive task. Please try again.');
        return;
      }

      // Update local state
      setClientTasks((currentTasks) => currentTasks.filter((task) => task.id !== taskId));
      setBacklogTasks((currentTasks) => currentTasks.filter((task) => task.id !== taskId));

      // Refresh archived tasks panel if it's open
      if (isArchivedSidepanelOpen) {
        await loadArchivedTasks();
      }

      // Refresh client data
      if (onTaskAdded) {
        onTaskAdded();
      }
    } catch (error) {
      console.error('Error archiving task:', error);
      setFetchError('Network error while archiving task. Please check your connection.');
    } finally {
      setIsArchivingTask(false);
      setArchiveModalOpen(false);
      setTaskToArchive(null);
    }
  };

  const handleRequestPermanentDelete = (taskId: string) => {
    setTaskToPermanentlyDelete(taskId);
    setPermanentDeleteModalOpen(true);
  };

  const handleDeleteArchivedTask = async () => {
    if (!taskToPermanentlyDelete) return;

    try {
      setIsDeletingArchivedTask(true);
      const { error } = await safeSupabaseQuery(() =>
        supabase.from('client_tasks').delete().eq('id', taskToPermanentlyDelete)
      );

      if (error) {
        console.error('Error deleting archived task:', error);
        setArchivedTasksError('Failed to delete archived task. Please try again.');
        return;
      }

      setArchivedTasks((tasks) => tasks.filter((task) => task.id !== taskToPermanentlyDelete));

      if (onTaskAdded) {
        onTaskAdded();
      }

      setPermanentDeleteModalOpen(false);
      setTaskToPermanentlyDelete(null);
    } catch (error) {
      console.error('Error deleting archived task:', error);
      setArchivedTasksError('Network error while deleting archived task. Please check your connection.');
    } finally {
      setIsDeletingArchivedTask(false);
    }
  };

  const handleAssignBacklogTask = async (taskId: string, userId: string, userName: string) => {
    try {
      setIsLoading(true);
      const { success, error } = await updateTask(taskId, { assignedTo: userId });
      if (!success) {
        console.error('Error assigning task:', error);
        setFetchError(`Failed to assign task: ${error || 'Please try again'}`);
        return;
      }
      const task = backlogTasks.find((t) => t.id === taskId);
      if (task) {
        const updated = { ...task, assigned_to: userId, assigned_to_name: userName };
        setBacklogTasks((tasks) => tasks.filter((t) => t.id !== taskId));
        setClientTasks((tasks) => [updated, ...tasks]);
      }
    } catch (err) {
      console.error('Error assigning task:', err);
      setFetchError('Network error while assigning task. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewTaskDetails = async (task: ClientTaskRow) => {
    try {
      // Fetch the complete task data to ensure we have all fields including notes
      const { data: fullTaskData, error: taskError } = await safeSupabaseQuery(() =>
        supabase.from('client_tasks').select('*').eq('id', task.id).single()
      );

      if (taskError) {
        console.error('Error fetching complete task data:', taskError);
        throw taskError;
      }

      setSelectedTask(fullTaskData as ClientTaskRow);
      setIsTaskDetailSidepanelOpen(true);
    } catch (error) {
      console.error('Error preparing task details:', error);
      setFetchError('Error loading task details. Please try again.');
    }
  };

  useUrlPanelSync(
    urlTask,
    isTaskDetailSidepanelOpen ? (selectedTask?.id ?? null) : null,
    true,
    (id) => void handleViewTaskDetails({ id } as ClientTaskRow),
    () => setIsTaskDetailSidepanelOpen(false),
  );

  const handleUpdateTask = async (taskId: string, updatedTask: UpdatedTaskInput) => {
    if (!updatedTask) return;

    // The drawer autosaves field by field and stays open. No page-level
    // loading state (it would flash the list on every keystroke); failures
    // reject so the drawer reverts the field and toasts.
    try {
      // Use the updateTask utility function
      const { success, error } = await updateTask(taskId, {
        title: updatedTask.title,
        status: updatedTask.status,
        dueDate: updatedTask.dueDate || null,
        assignedTo: updatedTask.assignedTo,
      });

      if (!success) {
        console.error('Error updating task:', error || 'Unknown error');
        throw new Error(error || 'Failed to update task');
      }

      // Update local state
      const updated = (task: ClientTaskRow): ClientTaskRow => ({
        ...task,
        title: updatedTask.title || task.title,
        status: updatedTask.status || task.status,
        due_date: updatedTask.dueDate || task.due_date,
        assigned_to: updatedTask.assignedTo,
        assigned_to_name:
          updatedTask.assignedToName ||
          (updatedTask.assignedTo
            ? users.find((u) => u.id === updatedTask.assignedTo)?.name
            : task.assigned_to_name),
      });

      const originalTask =
        [...clientTasks, ...backlogTasks].find((t) => t.id === taskId) || ({ id: taskId } as ClientTaskRow);
      const updatedItem = updated(originalTask);

      setClientTasks((current) => {
        const exists = current.some((t) => t.id === taskId);
        let next = current.map((t) => (t.id === taskId ? updatedItem : t));
        if (!updatedTask.assignedTo) {
          next = next.filter((t) => t.id !== taskId);
        } else if (!exists) {
          next = [...current, updatedItem];
        }
        return next;
      });

      setBacklogTasks((current) => {
        const exists = current.some((t) => t.id === taskId);
        let next = current.map((t) => (t.id === taskId ? updatedItem : t));
        if (updatedTask.assignedTo) {
          next = next.filter((t) => t.id !== taskId);
        } else if (!exists) {
          next = [...current, updatedItem];
        }
        return next;
      });

      // Refresh client data; the drawer stays open
      if (onTaskAdded) {
        onTaskAdded();
      }
    } catch (error) {
      console.error('Error updating task:', error);
      throw error;
    }
  };

  const handleClientTasksReorder = async (ordered: ClientTaskRow[]) => {
    setClientTasks(ordered);
    try {
      await Promise.all(
        ordered.map((task, index) =>
          supabase.from('client_tasks').update({ client_tasks_priority: index }).eq('id', task.id)
        )
      );
    } catch (err) {
      console.error('Error updating client task order:', err);
    }
  };

  const handleBacklogTasksReorder = async (ordered: ClientTaskRow[]) => {
    setBacklogTasks(ordered);
    try {
      await Promise.all(
        ordered.map((task, index) =>
          supabase.from('client_tasks').update({ client_backlog_priority: index }).eq('id', task.id)
        )
      );
    } catch (err) {
      console.error('Error updating backlog task order:', err);
    }
  };

  if (expandedSection !== 'tasks') return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      <Card
        eyebrow="Client Tasks"
        title={clientTasks.length > 0 ? `${clientTasks.length} open` : 'Nothing open'}
        style={{ overflow: 'visible' }}
        actions={
          <>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setArchivedTasksError(null);
                setIsArchivedSidepanelOpen(true);
              }}
            >
              <FontAwesomeIcon icon={faBoxArchive} style={{ fontSize: '12px', marginRight: '7px' }} />
              View Archived
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setIsTaskSidepanelOpen(true)}>
              <FontAwesomeIcon icon={faPlus} style={{ fontSize: '12px', marginRight: '7px' }} />
              Add Task
            </Button>
          </>
        }
      >
        {fetchError && (
          <div
            style={{
              marginBottom: 'var(--space-4)',
              padding: '10px 12px',
              borderRadius: 'var(--radius-md)',
              background: 'var(--status-down-soft)',
              border: '1px solid rgba(229, 72, 77, 0.25)',
              color: 'var(--danger-400)',
              fontSize: 'var(--text-sm)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 'var(--space-3)',
            }}
          >
            {fetchError}
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                setFetchError(null);
                // Force a re-fetch by toggling and re-opening the section
                toggleSection('');
                setTimeout(() => toggleSection('tasks'), 100);
              }}
            >
              Try Again
            </Button>
          </div>
        )}

        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '32px 0' }}>
            <FontAwesomeIcon
              icon={faSpinner}
              className="animate-spin"
              style={{ color: 'var(--text-faint)', fontSize: '20px' }}
            />
          </div>
        ) : (
          <ExistingTasksList
            clientTasks={clientTasks}
            isAdmin={isAdmin}
            handleStatusChange={handleStatusChange}
            handleArchiveTask={openArchiveModal}
            onViewTaskDetails={(task) => setUrlTask(task.id)}
            onReorder={handleClientTasksReorder}
          />
        )}
      </Card>

      <Card
        eyebrow="Backlog"
        title={backlogTasks.length > 0 ? `${backlogTasks.length} waiting` : 'Backlog is clear'}
        style={{ overflow: 'visible' }}
      >
        <BacklogTasksList
          tasks={backlogTasks}
          users={users}
          onAssign={handleAssignBacklogTask}
          onStatusChange={handleStatusChange}
          onArchive={openArchiveModal}
          onView={(task) => setUrlTask(task.id)}
          isAdmin={isAdmin}
          onReorder={handleBacklogTasksReorder}
        />
      </Card>

      {/* Archive Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={archiveModalOpen}
        onClose={() => setArchiveModalOpen(false)}
        onConfirm={() => taskToArchive && handleArchiveTask(taskToArchive)}
        isDeleting={isArchivingTask}
        title="Archive Task"
        message="Are you sure you want to archive this task? You can view it later in the archived tasks panel."
        confirmButtonLabel="Archive"
        processingLabel="Archiving..."
        titleIcon={faBoxArchive}
        confirmButtonIcon={faBoxArchive}
      />

      {/* Task Detail Sidepanel */}
      <TaskDetailSidepanel
        isOpen={isTaskDetailSidepanelOpen}
        onClose={() => setUrlTask(null)}
        selectedTask={selectedTask}
        users={users}
        onUpdateTask={handleUpdateTask}
        onArchiveTask={handleArchiveTask}
      />

      {/* Create Task Sidepanel */}
      <CreateTaskSidepanel
        isOpen={isTaskSidepanelOpen}
        onClose={() => setIsTaskSidepanelOpen(false)}
        client={client}
        onTaskAdded={onTaskAdded}
      />

      <ArchivedTasksSidepanel
        isOpen={isArchivedSidepanelOpen}
        onClose={() => {
          setIsArchivedSidepanelOpen(false);
          setPermanentDeleteModalOpen(false);
          setTaskToPermanentlyDelete(null);
          setArchivedTasksError(null);
        }}
        tasks={archivedTasks}
        isLoading={isLoadingArchivedTasks}
        error={archivedTasksError}
        onDelete={handleRequestPermanentDelete}
        onTaskUpdated={loadArchivedTasks}
      />

      <DeleteConfirmationModal
        isOpen={permanentDeleteModalOpen}
        onClose={() => setPermanentDeleteModalOpen(false)}
        onConfirm={handleDeleteArchivedTask}
        isDeleting={isDeletingArchivedTask}
        title="Delete Archived Task"
        message="Are you sure you want to permanently delete this task? This action cannot be undone."
      />
    </div>
  );
}
